import React, { FC, HTMLAttributes, MouseEvent, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { Option } from 'ia-react-core/~/interfaces/Option';
import { Address } from 'ia-react-core/~/interfaces/Address';
import startManualAddressEntryById from 'ia-react-core/~/actions/addressForm-fn/startManualAddressEntryById';
import { useDispatch } from 'react-redux';
import stopManualAddressEntryById from 'ia-react-core/~/actions/addressForm-fn/stopManualAddressEntryById';
import useScopedSelector from '../../../hooks/useScopedSelector';
import { ReactComponent as EditIcon } from '../../../icons/edit.svg';
import Spacing from '../../grid/Spacing';
import AddressFormat from '../../format/AddressFormat';
import IconButton from '../../buttons/IconButton';
import ClosableSection from '../../structure/ClosableSection';
import Link from '../../navigation/Link';
import AddressSuggestControl, { AddressSuggestControlProps } from '../AddressSuggestControl';
import ManualAddressForm, { ManualAddressFormProps } from '../ManualAddressForm';
import FormContext from '../FormContext';
import useTranslation from '../../../hooks/useTranslation';
import { StyledWrapper } from './AddressForm.styles';

export interface AddressFormProps extends
    Omit<ManualAddressFormProps, 'autoComplete' | 'name' | 'ref' | 'validationMessages' | keyof HTMLAttributes<HTMLFormElement>>,
    Omit<AddressSuggestControlProps, 'validate' | 'validators' | 'onSubmit' | 'name' | 'autoComplete' | 'defaultValue' | 'ref' | keyof HTMLAttributes<HTMLInputElement>> {
  /** Name of the address form. */
  name?: string;
  /** Start the form in the manual entry mode. */
  initialManualEntry?: boolean;
  /** Array of countries. */
  countries?: Option[];
  /** Array of countries specifically for the manual entry */
  manualEntryCountries?: Option[];
  /** Array of provinces to display as a dropdown. Used when the "provincesCountry" matches the current country. */
  provinces?: Option[];
  /** Whether to focus the search input. */
  autoFocus?: boolean;
  /** Array of sub building. */
  subBuildingOptions?: Option[];
  /** Array of deliveryOptions. */
  deliveryOptions?: Option[];
  manualAddressName?: string;
}

export interface AddressFormInternalProps {
  formId: number;
}

export interface AddressFormComposedProps extends Omit<AddressFormProps, 'formId'>, AddressFormInternalProps {}

const AddressFormDefaultProps = {
  initialManualEntry: false,
  name: 'address',
  autoFocus: true,
};

const AddressForm: FC<AddressFormProps> = ({
  formId, initialManualEntry, autoFocus, validators, validate, countries, manualEntryCountries, provinces, subBuildingOptions, deliveryOptions, ...props
}) => {
  const { name } = props;
  const { t } = useTranslation('Core.AddressForm');
  const dispatch = useDispatch();
  const isManualEntry = useScopedSelector(`${name}AddressForm.isManualEntry`);
  const { valuesDst, setControlValue } = useContext(FormContext);
  const address: Address = useScopedSelector(`${valuesDst}.${name}`);
  const openingRef = useRef(false);
  const closingRef = useRef(false);

  const hasAddress = useMemo(() => address && Object.keys(address).length !== 0, [address]);

  const resolvedAutoFocus = useMemo(() => (
    autoFocus && !('ontouchstart' in window)
  ), [autoFocus]);

  const [postCanadaCountries, setPostCanadaCountries] = useState<Option[]>();

  const resolvedCountries = useMemo(() => {
    if (manualEntryCountries) return manualEntryCountries;
    if (countries) return countries;
    return postCanadaCountries;
  }, [countries, manualEntryCountries, postCanadaCountries]);

  const handleEdit = useCallback(() => {
    dispatch(startManualAddressEntryById(formId));
    openingRef.current = true;
    setTimeout(() => {
      openingRef.current = false;
    }, 500);
  }, [dispatch, formId]);

  const handleAddressCancel = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    if (!openingRef.current) {
      event.preventDefault();
      dispatch(stopManualAddressEntryById(formId));
      closingRef.current = true;
      setTimeout(() => {
        setControlValue(name, undefined);
      });
      setTimeout(() => {
        closingRef.current = false;
      }, 500);
    }
  }, [dispatch, formId, setControlValue, name]);

  const handleCountriesLoad = useCallback((newCountries: Option[]) => {
    setPostCanadaCountries(newCountries);
  }, []);

  return (
    <StyledWrapper>
      {!isManualEntry && !hasAddress && (
        <AddressSuggestControl
          placeholder={t('placeholder')}
          {...props}
          name={name}
          countries={countries}
          subBuildingOptions={subBuildingOptions}
          deliveryOptions={deliveryOptions}
          onCountriesLoad={handleCountriesLoad}
        />
      )}
      {!isManualEntry && hasAddress && !closingRef.current && (
        <ClosableSection
          closeButtonLabel={t('cancelButton')}
          onClose={handleAddressCancel}
        >
          <ClosableSection.Header>
            <IconButton Icon={EditIcon} label={t('edit')} onClick={handleEdit} />
          </ClosableSection.Header>
          <ClosableSection.Content>
            <AddressFormat address={address} />
          </ClosableSection.Content>
        </ClosableSection>
      )}
      {isManualEntry && (
        <ClosableSection
          closeButtonLabel={t('cancelButton')}
          onClose={handleAddressCancel}
          p-b={4}
        >
          <ManualAddressForm
            {...props}
            formId={formId}
            autoFocus={resolvedAutoFocus}
            name={name}
            countries={resolvedCountries}
            provinces={provinces}
            validators={validators}
            validate={validate}
            subBuildingOptions={subBuildingOptions}
            deliveryOptions={deliveryOptions}
          />
        </ClosableSection>
      )}
      {!isManualEntry && !address && (
        <Spacing p-t={12}>
          <Link
            type="button"
            as="button"
            data-testid="manualEntryLink"
            onClick={handleEdit}
          >
            {t('manualEntry')}
          </Link>
        </Spacing>
      )}
    </StyledWrapper>
  );
};

AddressForm.defaultProps = AddressFormDefaultProps;

export default AddressForm;
